import { useEffect, useRef, useState } from "react";
import { initialCollapsedSections, readCollapsedSections, writeCollapsedSections } from "./sidebarCollapse";

/**
 * Which sidebar sections are folded in, persisted between visits.
 *
 * The section the user stands in is expanded on mount (deep links and full page loads included) and
 * whenever navigation *enters* a new section. Collapsing the section you are standing in sticks —
 * the auto-expand only fires on a change of active section, otherwise it could never be closed.
 */
export const useCollapsedSections = (sectionKeys: string[], activeKey: string | undefined) => {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set(initialCollapsedSections(sectionKeys, activeKey, readCollapsedSections())),
  );
  const prevActive = useRef<string | undefined>(undefined);

  useEffect(() => {
    const previous = prevActive.current;
    // Track sectionless routes too: leaving the menu and coming back into the same section is
    // entering it anew, and should auto-expand like any other section change.
    prevActive.current = activeKey;
    if (!activeKey || previous === activeKey) return;
    setCollapsed((prev) => {
      if (!prev.has(activeKey)) return prev;
      const next = new Set(prev);
      next.delete(activeKey);
      writeCollapsedSections([...next]);
      return next;
    });
  }, [activeKey]);

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      writeCollapsedSections([...next]);
      return next;
    });

  return { isCollapsed: (key: string) => collapsed.has(key), toggle };
};
